import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaTimesCircle } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { getDevConfig } from '../../config/devConfig';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import useAutoHeightAdjustment from '../../hooks/useAutoHeightAdjustment';
import ReCaptchaModal from '../ui/ReCaptchaModal';
import { 
  allServices, 
  validateForm, 
  focusFirstError, 
  getDefaultFormState, 
  getDefaultFormErrors 
} from '../../utils/formUtils';
import { sendEmail } from '../../utils/emailService';
import { useFormValidation } from '../../hooks/useFormValidation';
import emailjsConfig from '../../config/emailjsConfig';
import './Contact.css';
import '../ui/CheckboxContainer.css';

const Contact = () => {
  const SHOW_TEST_BUTTON = getDevConfig('SHOW_TEST_BUTTON');
  const location = useLocation();
  const titleRef = useScrollAnimation();
  const submissionSource = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('source') || 'website';
  }, [location.search]);
  const submissionSourceLabel = submissionSource === 'diego_card'
    ? 'Diego - Business Card'
    : 'Website';
  
  // Use the new auto height adjustment hook
  const { sameHeightRef, refresh: refreshHeights } = useAutoHeightAdjustment({
    containerSelector: '.contact-grid',
    elementSelector: '[data-same-height]',
    debounceDelay: 200,
    observeChildList: true,
    observeAttributes: true,
    observeCharacterData: true,
    subtree: true
  });
  
  // Use custom form validation hook
  const {
    formData,
    formErrors,
    setFormData,
    setFormErrors,
    handleChange,
    handleKeyDown,
    clearFieldError
  } = useFormValidation(getDefaultFormState(), getDefaultFormErrors());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [successMessageFading, setSuccessMessageFading] = useState(false);
  const [showCaptchaModal, setShowCaptchaModal] = useState(false);
  const [additionalFields, setAdditionalFields] = useState([]);
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServiceResults, setShowServiceResults] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);
  const successTimeoutRef = useRef(null);
  const selectedServicesRef = useRef(null);
  const serviceSearchRef = useRef(null);

  // Get available services (exclude already selected ones)
  const availableServices = useMemo(() => {
    return allServices.filter(service => 
      !formData.services.includes(service.value)
    );
  }, [formData.services]);

  // Get filtered services based on search input
  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return availableServices;
    return availableServices.filter(service =>
      service.label.toLowerCase().includes(serviceSearch.toLowerCase())
    );
  }, [availableServices, serviceSearch]);

  // Handle adding a service to the selected list
  const handleAddService = (serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, serviceValue]
    }));
    
    // Clear error when user selects a service
    if (formErrors.services) {
      setFormErrors(prev => ({
        ...prev,
        services: ''
      }));
    }
    
    // Trigger height refresh after service is added
    setTimeout(() => {
      if (refreshHeights) {
        refreshHeights();
      }
    }, 100);
  };

  // Handle removing a service from the selected list
  const handleRemoveService = (serviceValue) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(service => service !== serviceValue)
    }));
    
    // Trigger height refresh after service is removed
    setTimeout(() => {
      if (refreshHeights) {
        refreshHeights();
      }
    }, 100);
  };

  // Handle adding additional form fields
  const handleAddField = () => {
    const newField = {
      id: Date.now(),
      type: 'text',
      name: `additional_${Date.now()}`,
      placeholder: 'Additional Information',
      label: 'Additional Field'
    };
    setAdditionalFields(prev => [...prev, newField]);
  };

  // Handle removing additional form fields
  const handleRemoveField = (fieldId) => {
    setAdditionalFields(prev => prev.filter(field => field.id !== fieldId));
  };

  // Handle additional field changes
  const handleAdditionalFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [`additional_${fieldId}`]: value
    }));
  };

  // Handle service search input change
  const handleServiceSearchChange = (e) => {
    const value = e.target.value;
    setServiceSearch(value);
    // Always show results when there are available services, regardless of search input
    setShowServiceResults(availableServices.length > 0);
    setSelectedOptionIndex(-1); // Reset selection when typing
    
    // Clear services error when user starts typing
    if (formErrors.services) {
      setFormErrors(prev => ({
        ...prev,
        services: ''
      }));
    }
  };

  // Handle clearing service search
  const handleClearServiceSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setServiceSearch('');
    setShowServiceResults(false);
    setSelectedOptionIndex(-1);
    // Refocus the input after clearing
    if (serviceSearchRef.current) {
      serviceSearchRef.current.focus();
    }
  };

  // Handle service search input focus
  const handleServiceSearchFocus = () => {
    // Always show results when focused if there are available services
    setShowServiceResults(availableServices.length > 0);
    setSelectedOptionIndex(-1); // Reset selection on focus
  };

  // Handle service search input blur
  const handleServiceSearchBlur = (e) => {
    // Check if the blur is caused by clicking on a service result
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget.classList.contains('service-search-result')) {
      return; // Don't hide results if clicking on a service option
    }
    
    // Delay hiding results to allow for click events
    setTimeout(() => {
      setShowServiceResults(false);
      setSelectedOptionIndex(-1); // Reset selection on blur
    }, 200);
  };

  // Handle service selection from search results
  const handleServiceSelect = (serviceValue) => {
    handleAddService(serviceValue);
    setServiceSearch('');
    setSelectedOptionIndex(-1); // Reset selection
    
    // Keep dropdown open and maintain focus for continued selection
    setTimeout(() => {
      if (serviceSearchRef.current) {
        serviceSearchRef.current.focus();
        // Always show results - the availableServices will update automatically
        // due to the useMemo dependency on formData.services
        setShowServiceResults(true);
      }
    }, 100);
  };

  // Handle keyboard navigation for search results
  const handleServiceSearchKeyDown = (e) => {
    const currentResults = filteredServices.length > 0 ? filteredServices : availableServices;
    
    if (e.key === 'Escape') {
      setShowServiceResults(false);
      setServiceSearch('');
      setSelectedOptionIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentResults.length > 0) {
        const nextIndex = selectedOptionIndex < currentResults.length - 1 ? selectedOptionIndex + 1 : 0;
        setSelectedOptionIndex(nextIndex);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentResults.length > 0) {
        const prevIndex = selectedOptionIndex > 0 ? selectedOptionIndex - 1 : currentResults.length - 1;
        setSelectedOptionIndex(prevIndex);
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedOptionIndex >= 0 && selectedOptionIndex < currentResults.length) {
        const selectedService = currentResults[selectedOptionIndex];
        handleServiceSelect(selectedService.value);
      }
    }
    // Tab key is not handled here - it will naturally move to the next focusable element
  };

  // Refresh same-height when status messages change
  useEffect(() => {
    if (refreshHeights) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        refreshHeights();
      }, 50);
    }
  }, [submitStatus, refreshHeights]);

  // Refresh same-height when services change
  useEffect(() => {
    if (refreshHeights) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        refreshHeights();
      }, 100);
    }
  }, [formData.services, refreshHeights]);

  // Additional effect to watch selectedServicesRef changes
  useEffect(() => {
    if (selectedServicesRef.current && refreshHeights) {
      const observer = new MutationObserver(() => {
        // Debounced refresh for service tag changes
        setTimeout(() => {
          refreshHeights();
        }, 50);
      });

      observer.observe(selectedServicesRef.current, {
        childList: true,
        subtree: true,
        characterData: true
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [formData.services.length, refreshHeights]);

  // Auto-remove success message after 5 seconds
  useEffect(() => {
    if (submitStatus === 'success') {
      // Clear any existing timeout
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      
      // Start fade-out after 4.5 seconds
      successTimeoutRef.current = setTimeout(() => {
        setSuccessMessageFading(true);
        
        // Remove message completely after fade animation
        setTimeout(() => {
          setSubmitStatus(null);
          setSuccessMessageFading(false);
        }, 500); // Match CSS animation duration
      }, 4500);
    }

    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, [submitStatus]);



  // Validate form using shared utility
  const validateFormData = () => {
    const validation = validateForm(formData);
    setFormErrors(validation.errors);
    return { isValid: validation.isValid, errors: validation.errors };
  };

  const handleCaptchaVerify = async (value) => {
    // CAPTCHA completed, now send the email
    await sendEmailData();
    setShowCaptchaModal(false);
  };

  const sendEmailData = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Prepare email data for EmailJS
      const emailData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: Array.isArray(formData.services) ? formData.services.join(', ') : formData.services || '',
        message: formData.message,
        smsConsent: formData.smsConsent ? 'Yes' : 'No',
        source: submissionSource,
        sourceTag: submissionSourceLabel,
        type: 'contact'
      };
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Email sending timeout')), 30000); // 30 second timeout
      });
      
      const result = await Promise.race([
        sendEmail(emailData, 'contact'),
        timeoutPromise
      ]);
      
      if (result.success) {
        setIsSubmitting(false);
        setSubmitStatus('success');
        setFormData(getDefaultFormState());
        setFormErrors(getDefaultFormErrors());
        
        // Focus on success message after a short delay to ensure it's rendered
        setTimeout(() => {
          const successMessage = document.querySelector('#contact .status-message.success');
          if (successMessage) {
            successMessage.focus();
            successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form using shared utility
    const validation = validateFormData();
    if (!validation.isValid) {
      setSubmitStatus('validation-error');
      focusFirstError(validation.errors, { serviceSearch: serviceSearchRef.current }, 'contact');
      return;
    }
    
    // Show reCAPTCHA modal for verification
    setShowCaptchaModal(true);
  };

  const contactInfo = [
    {
      icon: FaPhone,
      title: 'Call',
      details: '313-631-6020',
      isClickable: true
    },
    {
      icon: FaEnvelope,
      title: 'Email',
      details: 'hi@rcroof.co',
      isClickable: true
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Text',
      details: '313-631-6020',
    },
    {
      icon: FaClock,
      title: 'Hours',
      details: 'Mon-Sat: 7AM-7PM'
    }
  ];

  const handlePhoneClick = () => {
    window.location.href = 'tel:+13136316020';
  };

  const handleEmailClick = () => {
    window.location.href = 'mailto:hi@rcroof.co';
  };

  return (
    <section id="contact" className="section contact" aria-labelledby="contact-title">
      <div className="container">
        <header className="contact-header">
          <h2 id="contact-title" ref={titleRef} className="contact-title">Get In Touch</h2>
          <p className="contact-subtitle">
            Ready to start your next project? Contact us today for a free consultation and let's bring your vision to life.
          </p>
        </header>
        
        <div className="contact-grid same-height">
          {/* Contact Information Card */}
          <div className="contact-info-card" data-same-height="1" same-height-device="d">
            <div className="contact-info-header">
              <h3>Contact Information</h3>
              <p>We're here to help you transform your space. Reach out using any method below.</p>
            </div>
            
            <div className="contact-methods">
              {contactInfo.map((info, index) => {
                const getClickHandler = () => {
                  if (!info.isClickable) return undefined;
                  if (info.title === 'Call') return handlePhoneClick;
                  if (info.title === 'Email') return handleEmailClick;
                  return undefined;
                };

                return (
                  <div 
                    key={index} 
                    className={`contact-method ${info.isClickable ? 'clickable-method' : ''}`}
                    onClick={getClickHandler()}
                    style={{ cursor: info.isClickable ? 'pointer' : 'default' }}
                  >
                    <div className="contact-method-icon">
                      <info.icon />
                    </div>
                    <div className="contact-method-content">
                      <h4>{info.title}</h4>
                      <p>{info.details}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Contact Form Card */}
          <div className="contact-form-card" data-same-height="1" same-height-device="d">
            <div className="form-header">
              <h3>Request a Free Quote</h3>
              <p className="pad-h-xs-4 margin-b-1">Fill out the form below and we'll get back to you within 24 hours.</p>
            </div>
            
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <input type="hidden" name="source" value={submissionSource} />
              <div>
                <div className="form-group">
                  <label htmlFor="contact-name" className="form-label">Full Name</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Full Name"
                    className={`form-input ${formErrors.name ? 'error' : ''}`}
                    aria-describedby={formErrors.name ? 'name-error' : undefined}
                    aria-invalid={!!formErrors.name}
                    title="Please enter only letters"
                    required
                  />
                  {formErrors.name && (
                    <span id="name-error" className="form-error" role="alert">{formErrors.name}</span>
                  )}
                </div>
              </div>
              
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact-email" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="contact-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  className={`form-input ${formErrors.email ? 'error' : ''}`}
                  aria-describedby={formErrors.email ? 'email-error' : undefined}
                  aria-invalid={!!formErrors.email}
                  required
                />
                {formErrors.email && (
                  <span id="email-error" className="form-error" role="alert">{formErrors.email}</span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="contact-phone" className="form-label">Phone Number</label>
                <input
                  type="tel"
                  id="contact-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Phone Number"
                  className={`form-input ${formErrors.phone ? 'error' : ''}`}
                  aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                  aria-invalid={!!formErrors.phone}
                  title="Please enter only numbers"
                  required
                />
                {formErrors.phone && (
                  <span id="phone-error" className="form-error" role="alert">{formErrors.phone}</span>
                )}
              </div>
            </div>

              <div>
              <div className="form-group">
                  {/* Selected Services Tags */}
                  {formData.services.length > 0 && (
                    <div className="selected-services-container">
                      <label className="selected-services-label">Selected Services:</label>
                      <div className="selected-services" ref={selectedServicesRef}>
                        {formData.services.map(serviceValue => {
                          const service = allServices.find(s => s.value === serviceValue);
                          return (
                            <div key={serviceValue} className="service-tag">
                              <span className="service-tag-text">{service?.label || serviceValue}</span>
                              <button
                                type="button"
                                className="service-tag-remove"
                                onClick={() => handleRemoveService(serviceValue)}
                                aria-label={`Remove ${service?.label || serviceValue}`}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Service Search Input */}
                  {availableServices.length > 0 && (
                    <div 
                      className="service-search-container"
                      onClick={(e) => {
                        // Prevent container clicks from triggering focus
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <label htmlFor="service-search-contact" className="form-label">
                        Services Needed
                      </label>
                      <div className="service-search-input-wrapper">
                        <input
                          ref={serviceSearchRef}
                          type="text"
                          id="service-search-contact"
                          name="service-search"
                          value={serviceSearch}
                          onChange={handleServiceSearchChange}
                          onFocus={handleServiceSearchFocus}
                          onBlur={handleServiceSearchBlur}
                          onKeyDown={handleServiceSearchKeyDown}
                          placeholder="Search and add services..."
                          className={`service-search-input ${formErrors.services ? 'error' : ''}`}
                          autoComplete="off"
                          aria-expanded={showServiceResults}
                          aria-haspopup="listbox"
                          aria-autocomplete="list"
                          aria-describedby={formErrors.services ? 'services-error' : 'services-help'}
                          aria-invalid={!!formErrors.services}
                          role="combobox"
                        />
                        {showServiceResults && (
                          <button
                            type="button"
                            className="service-search-clear-btn"
                            onClick={handleClearServiceSearch}
                            aria-label="Clear search"
                            title="Clear search"
                          >
                            <FaTimesCircle />
                          </button>
                        )}
                      </div>
                      
                      <div id="services-help" className="form-help-text sr-only">
                        Type to search and select services. You can add multiple services.
                      </div>
                      
                      {formErrors.services && (
                        <span id="services-error" className="form-error" role="alert">{formErrors.services}</span>
                      )}
                      
                      {/* Search Results Dropdown */}
                      {(showServiceResults || (serviceSearch.trim() && filteredServices.length === 0)) && (
                        <div 
                          className="service-search-results"
                          role="listbox"
                          aria-label="Available services"
                          id="service-search-listbox-contact"
                        >
                          {serviceSearch.trim() && filteredServices.length === 0 ? (
                            <div 
                              className="service-search-no-results"
                              role="status"
                              aria-live="polite"
                            >
                              No services found for "{serviceSearch}"
                            </div>
                          ) : (
                            (serviceSearch.trim() ? filteredServices : availableServices).map((service, index) => (
                              <div
                                key={service.value}
                                className={`service-search-result ${index === selectedOptionIndex ? 'selected' : ''}`}
                                onClick={() => handleServiceSelect(service.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleServiceSelect(service.value);
                                  }
                                }}
                                role="option"
                                tabIndex={-1}
                                aria-selected={index === selectedOptionIndex}
                                ref={(el) => {
                                  if (index === selectedOptionIndex && el) {
                                    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                                  }
                                }}
                              >
                                {service.label}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {availableServices.length === 0 && formData.services.length > 0 && (
                    <div className="no-services-message">
                      All services have been selected
                    </div>
                  )}
                  
                </div>
              </div>
              
              {/* Additional Dynamic Fields */}
              {additionalFields.map(field => (
                <div key={field.id} className="form-group">
                  <label htmlFor={field.name}>{field.label}</label>
                  <div className="additional-field-container">
                    <input
                      type={field.type}
                      id={field.name}
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={(e) => handleAdditionalFieldChange(field.id, e.target.value)}
                      placeholder={field.placeholder}
                      className="additional-field-input"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveField(field.id)}
                      className="additional-field-remove"
                      title="Remove field"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
              
              <div className="form-group">
                <label htmlFor="contact-message" className="form-label">Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us the address you need service at and provide any additional details here..."
                  className={`form-textarea ${formErrors.message ? 'error' : ''}`}
                  aria-describedby={formErrors.message ? 'message-error' : undefined}
                  aria-invalid={!!formErrors.message}
                  required
                ></textarea>
                {formErrors.message && (
                  <span id="message-error" className="form-error" role="alert">{formErrors.message}</span>
                )}
              </div>

              {/* SMS Consent Checkbox */}
              <div className="form-group">
                <div className="checkbox-container pad-h-3">
                  <input
                    type="checkbox"
                    id="contact-sms-consent"
                    name="smsConsent"
                    checked={formData.smsConsent || false}
                    onChange={handleChange}
                    className={`sms-consent-checkbox ${formErrors.smsConsent ? 'error' : ''}`}
                    aria-describedby="sms-consent-disclosure"
                    aria-invalid={!!formErrors.smsConsent}
                  />
                  <label htmlFor="contact-sms-consent" className="sms-consent-label">
                    <span className="checkbox-text">
                      <strong>Receive SMS Updates</strong>
                      <span className="disclosure-text">
                      By checking this box, you agree to receive SMS messages from RC Roofing. Message and data rates may apply. Frequency may vary. Reply STOP to unsubscribe or HELP for info. Messages may include updates, reminders, notifications, and promotions. View our Terms and Privacy Policy.<br />View our{' '}
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">terms and privacy policy</a>.
                      </span>
                    </span>
                  </label>
                </div>
                {formErrors.smsConsent && (
                  <span id="sms-consent-error" className="form-error" role="alert">{formErrors.smsConsent}</span>
                )}
              </div>
              
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  <span className="button-text">
                    {isSubmitting ? 'Sending...' : 'SEND MESSAGE'}
                  </span>
                </button>
                
                {/* TESTING: Button to add additional fields */}
                {SHOW_TEST_BUTTON && (
                  <button 
                    type="button" 
                    className="test-add-field-button"
                    onClick={handleAddField}
                  >
                    Add Field
                  </button>
                )}
                
                {/* TESTING: Button to add a service */}
                {SHOW_TEST_BUTTON && (
                  <button 
                    type="button" 
                    className="test-add-service-button"
                    onClick={() => {
                      if (availableServices.length > 0) {
                        handleAddService(availableServices[0].value);
                      }
                    }}
                    disabled={availableServices.length === 0}
                    style={{
                      background: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      padding: '18px 25px',
                      borderRadius: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      marginLeft: '15px',
                      opacity: availableServices.length === 0 ? 0.5 : 1
                    }}
                    onMouseOver={(e) => {
                      if (availableServices.length > 0) {
                        e.target.style.background = '#138496';
                        e.target.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (availableServices.length > 0) {
                        e.target.style.background = '#17a2b8';
                        e.target.style.transform = 'translateY(0)';
                      }
                    }}
                  >
                    Add Service
                  </button>
                )}
                
                {/* TESTING: Button to simulate API error */}
                {SHOW_TEST_BUTTON && (
                  <button 
                    type="button" 
                    className="test-error-button"
                    onClick={() => {
                      setIsSubmitting(true);
                      setTimeout(() => {
                        setIsSubmitting(false);
                        setSubmitStatus('error');
                      }, 1000);
                    }}
                    disabled={isSubmitting}
                  >
                    Test Error
                  </button>
                )}
              </div>
              
              <div className="form-disclaimer">
                <p>By submitting this form, you agree to receive communications from RC Roofing. We respect your privacy and will never share your information.</p>
              </div>
              
              {/* Developer indicator */}
              {SHOW_TEST_BUTTON && (
                <div className="dev-indicator">
                  <small>🔧 Developer Mode: Test buttons enabled - Try adding/removing fields and services to see automatic height adjustment</small>
                </div>
              )}
              
              {/* Success Message */}
              {submitStatus === 'success' && (
                <div 
                  className={`status-message success ${successMessageFading ? 'fade-out' : ''}`}
                  tabIndex="-1"
                  role="alert"
                  aria-live="polite"
                >
                  <span className="status-icon">✓</span>
                  <span className="status-text">Your message has been sent successfully!</span>
                </div>
              )}
              
            </form>
          </div>
        </div>
      </div>
      
      <ReCaptchaModal 
        isOpen={showCaptchaModal}
        onVerify={handleCaptchaVerify}
        isLoading={isSubmitting}
      />
    </section>
  );
};

export default Contact;
